/** Codici stabili nel body degli errori 4xx: il frontend discrimina su questi, mai sul testo. */
export const DEADLINE_ERROR_CODES = {
  LOCKED_BY_CONSULTANT: 'DEADLINE_LOCKED_BY_CONSULTANT',
  CLIENT_UPLOAD_DISABLED: 'DEADLINE_CLIENT_UPLOAD_DISABLED',
  DELETE_NOT_ALLOWED: 'DEADLINE_DELETE_NOT_ALLOWED',
  MANAGED_BY_OTHER_CONSULTANT: 'DEADLINE_MANAGED_BY_OTHER_CONSULTANT',
  NOT_MANAGED_BY_CONSULTANT: 'DEADLINE_NOT_MANAGED_BY_CONSULTANT',
  CLIENT_SUBSCRIPTION_BLOCKED: 'CLIENT_SUBSCRIPTION_BLOCKED',
  VALIDITY_REQUIRED: 'DEADLINE_VALIDITY_REQUIRED',
  ISSUED_AT_REQUIRED: 'DEADLINE_ISSUED_AT_REQUIRED',
  RENEWAL_DUE_DATE_REQUIRED: 'DEADLINE_RENEWAL_DUE_DATE_REQUIRED',
  RENEWAL_DUE_DATE_IN_PAST: 'DEADLINE_RENEWAL_DUE_DATE_IN_PAST',
  /** Il documento letto in anteprima non è più disponibile (scaduto, già usato, di un altro cliente): va scelto di nuovo. */
  UPLOAD_TOKEN_INVALID: 'DEADLINE_UPLOAD_TOKEN_INVALID',
  /** Il documento appartiene già a una scadenza (oggi 409 con il solo messaggio). */
  DOCUMENT_ALREADY_LINKED: 'DEADLINE_DOCUMENT_ALREADY_LINKED',
  /** Creazione del consulente: se il documento lo carica lui (`clientCanUpload` false), va allegato subito. */
  DOCUMENT_REQUIRED: 'DEADLINE_DOCUMENT_REQUIRED',
  /** Creazione del consulente: il documento che carica lui appartiene a un solo cliente. */
  CONSULTANT_UPLOAD_SINGLE_CLIENT: 'DEADLINE_CONSULTANT_UPLOAD_SINGLE_CLIENT',
  /** La firma chiesta all'azienda la appone un suo membro, mai il Consulente (403). */
  SIGNATURE_RESERVED_TO_COMPANY: 'DOCUMENT_SIGNATURE_RESERVED_TO_COMPANY',
  /**
   * La firma si chiede solo a un'azienda con abbonamento attivo o in prova, non creata dal
   * consulente e con un titolare o un manager che firma dall'app (400).
   */
  COMPANY_SIGNATURE_UNAVAILABLE: 'DEADLINE_COMPANY_SIGNATURE_UNAVAILABLE',
} as const;

/**
 * Firma attesa su un documento (colonna `SignedDocument.signatureRequest`).
 * `EXPECTED`: upload dell'archivio, firma attesa come sempre; `COMPANY`: il consulente chiede la
 * firma dell'azienda; `NONE`: file dello scadenzario senza firma richiesta ("Caricato").
 */
export const DOCUMENT_SIGNATURE_REQUEST_VALUES = ['EXPECTED', 'COMPANY', 'NONE'] as const;
export type DocumentSignatureRequestValue = (typeof DOCUMENT_SIGNATURE_REQUEST_VALUES)[number];

/**
 * Firma chiesta all'azienda sull'ultimo file di una scadenza, letta dal consulente e dal cliente.
 * `NOT_REQUESTED` per ogni file che non la chiede, compresi gli upload d'archivio (`EXPECTED`).
 */
export const DEADLINE_DOCUMENT_SIGNATURE_STATES = [
  'NOT_REQUESTED',
  'AWAITING_SIGNATURE',
  'SIGNED',
] as const;
export type DeadlineDocumentSignatureState = (typeof DEADLINE_DOCUMENT_SIGNATURE_STATES)[number];

interface DocumentSignatureFacts {
  status: string;
  signatureRequest: DocumentSignatureRequestValue;
}

/** Stato della firma chiesta all'azienda (`COMPANY`) sul file stesso, a prescindere dalla riga. */
export function resolveDocumentSignatureState(
  document: DocumentSignatureFacts,
): DeadlineDocumentSignatureState {
  if (document.signatureRequest !== 'COMPANY') {
    return 'NOT_REQUESTED';
  }
  return document.status === 'SIGNED' ? 'SIGNED' : 'AWAITING_SIGNATURE';
}

/** "Da firmare": non firmato e qualcuno ne attende la firma (archivio o azienda), mai un "Caricato". */
export function isDocumentAwaitingSignature(document: DocumentSignatureFacts): boolean {
  return document.status === 'PENDING' && document.signatureRequest !== 'NONE';
}

/**
 * Valori di `DeadlineCycle.note` per i cicli `MANAGEMENT_CHANGED`: il verso del
 * passaggio non ha una colonna propria, il banner "da riprendere" lo legge da qui.
 */
export const MANAGEMENT_CHANGE_NOTES = {
  RELEASED_TO_CLIENT: 'MANAGEMENT_TO_CLIENT',
  CLAIMED_BY_CONSULTANT: 'MANAGEMENT_TO_CONSULTANT',
  REASSIGNED_TO_TEAMMATE: 'MANAGEMENT_TO_TEAMMATE',
} as const;

export const CONSULTANT_BULK_MAX_TENANTS = 50;
export const CONSULTANT_CLAIM_MAX_DEADLINES = 200;

/** Several deadlines of one type for the same client are allowed: no skip for an existing one. */
export const CONSULTANT_BULK_SKIP_REASONS = ['subscription_blocked', 'out_of_perimeter'] as const;
export type ConsultantBulkSkipReason = (typeof CONSULTANT_BULK_SKIP_REASONS)[number];

export const CONSULTANT_CLAIM_SKIP_REASONS = [
  'out_of_perimeter',
  'already_managed',
  'archived',
  'subscription_blocked',
] as const;
export type ConsultantClaimSkipReason = (typeof CONSULTANT_CLAIM_SKIP_REASONS)[number];

/** Spec §11.1: risposta alla domanda "Questo documento ha una scadenza?". */
export const DEADLINE_RECURRENCE_CHOICES = ['NONE', 'ONE_OFF', 'RECURRING'] as const;
export type DeadlineRecurrenceChoice = (typeof DEADLINE_RECURRENCE_CHOICES)[number];

export const DEADLINE_RECURRENCE_FILTERS = ['RECURRING', 'ONE_OFF'] as const;
export type DeadlineRecurrenceFilter = (typeof DEADLINE_RECURRENCE_FILTERS)[number];

export const DEADLINE_RENEWAL_KINDS = ['RECURRING', 'ONE_OFF'] as const;
export type DeadlineRenewalKind = (typeof DEADLINE_RENEWAL_KINDS)[number];

/** Valori di `DeadlineCycle.note` per i cicli di cambio ricorrenza e di chiusura. */
export const DEADLINE_CYCLE_NOTES = {
  RECURRENCE_CHANGED: 'RECURRENCE_CHANGED',
  DOCUMENT_TYPE_CHANGED: 'DOCUMENT_TYPE_CHANGED',
  NO_LONGER_NEEDED: 'NO_LONGER_NEEDED',
} as const;

export const DEADLINE_ARCHIVE_REASONS = ['NO_LONGER_NEEDED'] as const;
export type DeadlineArchiveReason = (typeof DEADLINE_ARCHIVE_REASONS)[number];

export const DEADLINE_VALIDITY_SUGGESTION_SOURCES = ['LAST_USED', 'CATALOG'] as const;
export type DeadlineValiditySuggestionSource =
  (typeof DEADLINE_VALIDITY_SUGGESTION_SOURCES)[number];
